import React, { createContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import apiClient from '../api/client';
import type { LoginResponse } from '../api/types';

interface User {
  id: string;
  // Guests have no email — kept nullable to match backend CurrentUserResponse.
  email: string | null;
  display_name: string;
  role: 'guest' | 'participant' | 'coordinator' | 'creator' | 'org_admin';
  organization_id: string;
  organization_slug: string;
}

export type LoginStatus = 'success' | '2fa_required' | '2fa_setup_required';

export interface LoginResult {
  status?: LoginStatus;
  setupToken?: string;
  error?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, totpCode?: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  needsTwoFactorSetup: () => boolean;
}

const SETUP_TOKEN_KEY = 'clavis_2fa_setup_token';

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = async () => {
    try {
      // Try to fetch current user to verify authentication
      const response = await apiClient.get('/api/auth/me');
      setUser(response.data);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email: string, password: string, totpCode?: string): Promise<LoginResult> => {
    try {
      const payload: { email: string; password: string; totp_code?: string } = {
        email,
        password,
      };

      if (totpCode) {
        payload.totp_code = totpCode;
      }

      const { data } = await apiClient.post<LoginResponse>('/api/auth/login', payload);

      if (data.status === '2fa_required') {
        return { status: '2fa_required' };
      }

      if (data.status === '2fa_setup_required') {
        if (data.setup_token) {
          sessionStorage.setItem(SETUP_TOKEN_KEY, data.setup_token);
        }
        return { status: '2fa_setup_required', setupToken: data.setup_token };
      }

      // On successful login, fetch the full user from /auth/me so we get
      // organization_slug (and any future fields) from a single source of truth.
      if (data.status === 'success') {
        await checkAuth();
      }
      return { status: 'success' };
    } catch (error) {
      const message = (error as { response?: { data?: { detail?: string } }; detail?: string }).response?.data?.detail
        ?? (error as { detail?: string }).detail
        ?? 'Login failed';
      return { error: message };
    }
  };

  const logout = async () => {
    try {
      await apiClient.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      sessionStorage.removeItem(SETUP_TOKEN_KEY);
      setUser(null);
    }
  };

  const needsTwoFactorSetup = () => sessionStorage.getItem(SETUP_TOKEN_KEY) !== null;

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    checkAuth,
    needsTwoFactorSetup,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
